import i18n from 'helpers/i18n';
import messages from './messages';
import { keys, map } from 'ramda';
import useAsync from 'hooks/useAsync';
import { useLinkClickHandler } from 'react-router-dom';
import React, { useEffect } from 'react';
import { getColor, getLabel } from './utils';
import { Button, Text } from '@teachstonellc/component-library';
import ReportLoader from 'components/Reports/ReportLoader';
import ReliabilitySummary from './ReliabilitySummary';
import { getReliabilityReport, getKnowledgeReport } from 'actions/trainings';

import {
  Container,
  Header,
  MobileHeader,
  Actions,
  MobileActions,
  Section,
  ErrorSection,
  StyledAnchor
} from './Styled';

interface ReliabilityReportProps {
  /** Training ID the report belongs to. */
  trainingId: number;
  testType?: 'knowledge' | 'reliability';
}

const ReliabilityReport: React.FC<ReliabilityReportProps> = ({
  trainingId,
  testType = 'reliability'
}) => {
  const { run, data: reportData, isIdle, isLoading } = useAsync();
  const reportPath = `/reports/${testType}/${trainingId}`;
  const onReportClick = useLinkClickHandler<HTMLAnchorElement>(reportPath);

  useEffect(() => {
    const action =
      testType === 'reliability' ? getReliabilityReport : getKnowledgeReport;
    run(action(trainingId));
  }, [run, testType, trainingId]);

  if (isIdle || isLoading) {
    return <ReportLoader label={i18n.ft(messages.loading)} />;
  }

  const ErrorMessage = () => (
    <ErrorSection>
      <div className="font-bold text-red-500 text-md">
        {i18n.ft(messages.errorMessage)}{' '}
        <StyledAnchor
          target="_blank"
          color="red"
          href="mailto:contact@teachstone.com"
          rel="noreferrer"
        >
          contact@teachstone.com
        </StyledAnchor>
      </div>
    </ErrorSection>
  );

  return (
    <>
      {testType === null ? (
        <ErrorMessage />
      ) : (
        <Section>
          <Container>
            <MobileHeader>
              <Text as="h2" className="font-bold text-blue-600 text-lg">
                {testType === 'reliability'
                  ? i18n.ft(messages.header)
                  : i18n.ft(messages.headerKnowledge)}
              </Text>
            </MobileHeader>

            <Header>
              <Text as="h2" className="font-bold text-blue-600 text-lg">
                {testType === 'reliability'
                  ? i18n.ft(messages.header)
                  : i18n.ft(messages.headerKnowledge)}
              </Text>

              <Actions>
                <Button
                  as="a"
                  href={reportPath}
                  onClick={onReportClick}
                  shape="rounded"
                  size="small"
                >
                  {testType === 'reliability'
                    ? i18n.ft(messages.viewReport)
                    : i18n.ft(messages.viewKnowledgeReport)}
                </Button>
              </Actions>
            </Header>

            {map(dataKey => {
              const data = reportData[dataKey];
              const key = String(dataKey) as any;

              return (
                <ReliabilitySummary
                  key={key}
                  testType={testType}
                  passed={data.total_passed}
                  failed={data.total_taken - data.total_passed}
                  color={getColor(key)}
                  label={getLabel(key)}
                  inline={keys(reportData).length === 1}
                />
              );
            }, keys(reportData))}

            <MobileActions>
              <Button
                as="a"
                href={reportPath}
                onClick={onReportClick}
                fullWidth
                shape="rounded"
                size="small"
              >
                {i18n.ft(
                  testType === 'knowledge'
                    ? messages.viewKnowledgeReport
                    : messages.viewReport
                )}
              </Button>
            </MobileActions>
          </Container>
        </Section>
      )}
    </>
  );
};

export default ReliabilityReport;
